import * as React from 'react';
import { useState, useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import EditIcon from '@mui/icons-material/Edit';
import EditOffIcon from '@mui/icons-material/EditOff';
import { visuallyHidden } from '@mui/utils';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { makeStyles } from '@mui/styles';
import ProcedureAndTriggerForm from './procedureAndTriggerForm';
import DateComponent from '../../../Registrationform/components/dateComponent';
import { useDispatch, useSelector } from 'react-redux';
import { setPatientData } from '../../../../actions';
import GeneralDetails from '../GeneralDetails';
import moment from 'moment';
import dayjs from 'dayjs';
import EtTypes from './etTypes';
// Styles for the table
const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  addRowButton: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '10px',
  },
  saveButton: {
    display: 'flex',
    width: '150px',
    justifyContent: 'center',
    marginTop: '10px',
  },
  inputField: {
    width: '80px',
    border: 'none',
    padding: '1px',
    boxSizing: 'border-box',
    fontSize: '12px',
  },
  fieldset: {},
});

// Styled components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    padding: 2,
    textAlign: 'start',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 10,
    padding: 2,
    textAlign: 'start',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

// Default row structure for the new component
const newDefaultRow = {
  date: '',
  day: '',
  e2: '',
  p4: '',
  endometrialThickness: '',
  medicine1: '',
  dose1: '',
  medicine2: '',
  dose2: '',
  medicine3: '',
  dose3: '',
  comments: '',
};

// Head cells definition for the new component
const newHeadCells = [
  { id: 'srn', numeric: false, disablePadding: true, label: 'SRN' },
  { id: 'date', numeric: false, disablePadding: true, label: 'Date' },
  { id: 'day', numeric: false, disablePadding: false, label: 'Day' },
  { id: 'e2', numeric: false, disablePadding: false, label: 'E2' },
  { id: 'p4', numeric: false, disablePadding: false, label: 'P4' },
  {
    id: 'endometrialThickness',
    numeric: false,
    disablePadding: false,
    label: 'Endometrial Thickness',
  },
  {
    id: 'medicine1',
    numeric: false,
    disablePadding: false,
    label: 'Medicine 1',
  },
  { id: 'dose1', numeric: false, disablePadding: false, label: 'Dose 1' },
  {
    id: 'medicine2',
    numeric: false,
    disablePadding: false,
    label: 'Medicine 2',
  },
  { id: 'dose2', numeric: false, disablePadding: false, label: 'Dose 2' },
  {
    id: 'medicine3',
    numeric: false,
    disablePadding: false,
    label: 'Medicine 3',
  },
  { id: 'dose3', numeric: false, disablePadding: false, label: 'Dose 3' },
  { id: 'comments', numeric: false, disablePadding: false, label: 'Comments' },
];

// EnhancedTableHead component for the new component
function FetSheetHeader(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
    showCheckboxes,
  } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {showCheckboxes && (
          <TableCell padding="checkbox">
            <Checkbox
              color="primary"
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
              inputProps={{
                'aria-label': 'select all rows',
              }}
            />
          </TableCell>
        )}
        {newHeadCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={'none'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// EnhancedTableToolbar component for the new component
function FetsheetToolBar(props) {
  const { numSelected, toggleCheckboxes, showCheckboxes, handleSaveData } =
    props;
  const classes = useStyles();

  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: '1 1 100%' }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: '1 1 100%' }}
          variant="h6"
          id="tableTitle"
          component="div"
        >
          FET Sheet
        </Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={showCheckboxes ? 'Hide checkboxes' : 'Show checkboxes'}>
        <IconButton onClick={toggleCheckboxes}>
          {showCheckboxes ? <EditOffIcon /> : <EditIcon />}
        </IconButton>
      </Tooltip>
      {/* <div className={classes.saveButton}>
        <Button variant="contained" color="primary" onClick={handleSaveData}>
          Save Data
        </Button>
      </div> */}
    </Toolbar>
  );
}

// New component
export default function FetSheet() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const patientData = useSelector(
    (state) => state.clinicalReducer.currentCycle,
  );

  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('date');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  // const [rows, setRows] = React.useState(Array(5).fill().map(() => ({ ...newDefaultRow })));
  const [rows, setRows] = useState([]);
  const [showCheckboxes, setShowCheckboxes] = React.useState(false);

  const [selectedDate, setSelectedDate] = useState(patientData.fetSheet.date);

  useEffect(() => {
    if (patientData) {
      setRows(patientData.fetSheet.tableData);
    }
  }, [patientData]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = rows.map((n, index) => index);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleCheckboxClick = (event, index) => {
    event.stopPropagation();
    const selectedIndex = selected.indexOf(index);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, index);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  const dateHandler = (date, index) => {
    const formattedDate = date ? date.format('YYYY-MM-DD') : '';

    const updatedRows = [...rows];
    updatedRows[index].date = formattedDate;
    setRows(updatedRows);

    dispatch(setPatientData(`currentCycle.fetSheet.tableData`, [...rows]));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };
  const handleInputChange = (e, rowIndex, field) => {
    const { name, value } = e.target;
    const updatedRows = [...rows];
    updatedRows[rowIndex][field] = value;
    setRows(updatedRows);

    dispatch(setPatientData(`currentCycle.fetSheet.tableData`, [...rows]));
  };

  const handleAddRow = () => {
    const newRow = {
      date: '',
      day: '',
      e2: '',
      p4: '',
      endometrialThickness: '',
      medicine1: '',
      dose1: '',
      medicine2: '',
      dose2: '',
      medicine3: '',
      dose3: '',
      comments: '',
    };

    setRows([...rows, newRow]);
    dispatch(
      setPatientData('currentCycle.fetSheet.tableData', [...rows, newRow]),
    );
  };

  const handleSaveData = () => {
    const filledRows = rows.filter((row) =>
      Object.values(row).some((value) => value !== ''),
    );

    // You can replace the above console.log with an API call or any other logic to save the data
  };

  const toggleCheckboxes = () => {
    setShowCheckboxes(!showCheckboxes);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () => rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, rows, patientData],
  );

  return (
    <Box sx={{ width: '100%' }}>
      <div
        className="cycle-header"
        style={{
          margin: '10px 10px 0px 0px',
          display: 'flex',
          flexDirection: 'row-reverse',
        }}
      >
        {patientData.createdAt ? (
          <h6>
            Created On:{' '}
            {patientData.createdAt
              ? dayjs(patientData.createdAt).format('DD-MM-YYYY')
              : ''}
          </h6>
        ) : null}
      </div>
      <GeneralDetails
        patientData={patientData}
        setPatientData={setPatientData}
      />
      <EtTypes />
      <Paper sx={{ width: '100%', mb: 2 }}>
        <FetsheetToolBar
          numSelected={selected.length}
          toggleCheckboxes={toggleCheckboxes}
          showCheckboxes={showCheckboxes}
          handleSaveData={handleSaveData}
        />
        <TableContainer>
          <Table
            sx={{ minWidth: 650 }}
            size={dense ? 'small' : 'medium'}
            aria-label="a dense table"
            style={{ fontSize: '8px' }}
          >
            <FetSheetHeader
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
              showCheckboxes={showCheckboxes}
            />
            <TableBody>
              {visibleRows.map((row, index) => {
                const isItemSelected = selected.includes(index);
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <StyledTableRow
                    hover
                    // onClick={(event) => handleClick(event, index)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={index}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    {showCheckboxes && (
                      <TableCell
                        padding="checkbox"
                        onClick={(event) => handleCheckboxClick(event, index)}
                      >
                        <Checkbox
                          color="primary"
                          checked={isItemSelected}
                          inputProps={{
                            'aria-labelledby': labelId,
                          }}
                        />
                      </TableCell>
                    )}
                    <StyledTableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      style={{ textAlign: 'center' }}
                    >
                      {index + 1}
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <DateComponent
                        selectedDate={
                          row.date ? dayjs(row.date, 'YYYY-MM-DD') : null
                        }
                        handleDateChange={(date) => dateHandler(date, index)}
                      />
                      {/* <TextField className={classes.inputField} value={row.date} onChange={(e) => handleInputChange(e, index, 'date')} /> */}
                    </StyledTableCell>
                    <StyledTableCell align="right" padding="none">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].day}
                        onChange={(e) => handleInputChange(e, index, 'day')}
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right" padding="none">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].e2}
                        onChange={(e) => handleInputChange(e, index, 'e2')}
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].p4}
                        onChange={(e) => handleInputChange(e, index, 'p4')}
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={
                          patientData.fetSheet.tableData[index]
                            .endometrialThickness
                        }
                        onChange={(e) =>
                          handleInputChange(e, index, 'endometrialThickness')
                        }
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].medicine1}
                        onChange={(e) =>
                          handleInputChange(e, index, 'medicine1')
                        }
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].dose1}
                        onChange={(e) => handleInputChange(e, index, 'dose1')}
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].medicine2}
                        onChange={(e) =>
                          handleInputChange(e, index, 'medicine2')
                        }
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].dose2}
                        onChange={(e) => handleInputChange(e, index, 'dose2')}
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].medicine3}
                        onChange={(e) =>
                          handleInputChange(e, index, 'medicine3')
                        }
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].dose3}
                        onChange={(e) => handleInputChange(e, index, 'dose3')}
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                    <StyledTableCell align="right">
                      <TextField
                        className={classes.inputField}
                        value={patientData.fetSheet.tableData[index].comments}
                        onChange={(e) =>
                          handleInputChange(e, index, 'comments')
                        }
                        // multiline
                        rows={2}
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
              {emptyRows > 0 && (
                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              style={{ height: 'fit-content' }}
              onClick={() => handleAddRow()}
            >
              Add Row
            </Button>
            <FormControlLabel
              control={<Switch checked={dense} onChange={handleChangeDense} />}
              label="Dense padding"
            />
          </div>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Paper>
      <ProcedureAndTriggerForm
        type={'fet'}
        data={{
          procedureDate: '',
          procedureTime: '',
          triggerDate: '',
          triggerTime: '',
        }}
      />
    </Box>
  );
}
